import {
    LineChartProperty,
    LineDataLabelProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/line-chart';
import { UpDownBarType } from '../../../Interface/chart-v3/sub-chart/stock-chart.interface';
import { HighLowLineProperty } from './geometry/high-low-line';
import { UpDownBarProperty } from './geometry/up-down-bars';
import type { IDataItemProperty } from './data-item/data-item.interface';
import type { IStockChart } from '../../../Interface/chart-v3/sub-chart';
import type { IChartShapeProperty } from '../chart-shape-property.interface';
import type {
    HighLowLinesChartDataItem,
    UpDownBarsChartDataItem,
    IStockChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/stock-chart.interface';
import type {
    IDataLabel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';

export class StockChartProperty extends LineChartProperty<IStockChart> implements IStockChartProperty {
    public highLowLinesData?: HighLowLinesChartDataItem[];
    public upDownBarsData?: UpDownBarsChartDataItem[];
    private highLowLineProperty: IChartShapeProperty | undefined;
    private upBarProperty: IChartShapeProperty | undefined;
    private downBarProperty: IChartShapeProperty | undefined;

    public init() {
        super.init();
        this.initStockData();
        this.initHighLowLineProperty();
        this.initUpDownBarProperty();
        return this;
    }

    public getDataLabelProperty(dataLabel: IDataLabel, dataItem: IDataItemProperty) {
        return new StockDataLabelProperty(dataLabel, this.context).init(dataItem);
    }

    public getHighLowLines() {
        return this.highLowLineProperty;
    }

    public getUpDownBars(barType: UpDownBarType) {
        if (barType === UpDownBarType.DOWN) {
            return this.downBarProperty;
        }
        return this.upBarProperty;
    }

    private initStockData() {
        const hasUpDownBars = !!(this as IStockChartProperty).upDownBars;
        const hasHighLowLines = !!(this as IStockChartProperty).highLowLines;

        // 初始化高低线和涨跌柱数据
        let startList;
        let endList;
        let maxList;
        let minList;

        if (this.series.length === 4) {
            // 有开盘、收盘、盘高、盘低数据，ser的顺序为：[开盘，盘高, 盘低，收盘]
            [startList, maxList, minList, endList] = [0, 1, 2, 3]
                .map(serIndex => this.data.filter(dataItem => dataItem.serIndex === serIndex));
        } else if (this.series.length === 3) {
            // 有收盘、盘高、盘低数据，ser的顺序为： [盘高, 盘低，收盘]
            [maxList, minList, endList] = [0, 1, 2]
                .map(serIndex => this.data.filter(dataItem => dataItem.serIndex === serIndex));
        }

        if (hasHighLowLines && maxList && minList) {
            this.highLowLinesData = maxList?.map((item, index) => ({
                category: item.category,
                value: [item.value, minList[index].value],
                chartId: this.id,
            }));
        }
        if (hasUpDownBars && startList && endList) {
            this.upDownBarsData = startList?.map((item, index) => ({
                category: item.category,
                value: [item.value, endList[index].value],
                chartId: this.id,
                barType: item.value - endList[index].value > 0 ? 'downBars' : 'upBars',
            }));
        }
    }

    private initHighLowLineProperty(): IChartShapeProperty | void {
        if (this.uiData.highLowLines) {
            this.highLowLineProperty = new HighLowLineProperty(this.uiData.highLowLines, this.context);
        }
    }

    private initUpDownBarProperty(): IChartShapeProperty | void {
        if (this.uiData.upDownBars?.upBars) {
            this.upBarProperty = new UpDownBarProperty(this.uiData.upDownBars.upBars, this.context)
                .init(UpDownBarType.UP);
        }
        if (this.uiData.upDownBars?.downBars) {
            this.downBarProperty = new UpDownBarProperty(this.uiData.upDownBars.downBars, this.context)
                .init(UpDownBarType.DOWN);
        }
    }
}

class StockDataLabelProperty extends LineDataLabelProperty {}
